'use client';

import React, { useEffect, useState } from 'react';
import { supabase } from '../../supabaseClient';
import { 
  ChevronDownIcon, 
  ChevronUpIcon, 
  ClockIcon, 
  DocumentIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  DocumentDuplicateIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';

export default function ExportHistory({ projectId, userId }) {
  const [exportHistory, setExportHistory] = useState([]);
  const [openAccordions, setOpenAccordions] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const loadExportHistory = async () => {
    const isInitialLoad = loading;
  
    if (!isInitialLoad) setRefreshing(true);
    setError(null);
  
    // Validate required parameters
    if (!projectId) {
      setError('Project ID is required');
      setLoading(false);
      return;
    }
  
    if (!userId) {
      setError('User ID is required');
      setLoading(false); 
      return;
    }
  
    try {
      console.log('Executing query for project:', projectId, 'user:', userId);
      
      // Query the view directly - much simpler!
      const { data: logs, error: logError } = await supabase
        .from('export_history_by_project')
        .select('export_batch_id, measurement_id, integration_project_code, status, message, export_id, created_at, integration')
        .eq('user_id', userId)
        .eq('tech_connect_project_id', projectId)
        .order('created_at', { ascending: false });
  
      if (logError) {
        console.error('Query error:', logError);
        throw new Error('Failed to fetch export history.');
      }
  
      // Handle case where no logs are found
      if (!logs || logs.length === 0) {
        console.log('No export logs found for project:', projectId);
        setExportHistory([]);
        return;
      }
  
      // Group logs by batch ID
      const groupedLogs = logs.reduce((acc, log) => {
        const batchId = log.export_batch_id;
        if (!acc[batchId]) {
          acc[batchId] = {
            export_batch_id: batchId,
            created_at: log.created_at,
            integration: log.integration,
            logs: [],
            status: 'success',
          };
        }
        acc[batchId].logs.push({
          ...log,
          project_code: log.integration_project_code  // Map to expected field name
        });
        
        // Determine overall batch status
        if (log.status === 'error') {
          acc[batchId].status = 'error';
        } else if (log.status === 'pending' && acc[batchId].status !== 'error') {
          acc[batchId].status = 'pending';
        }
        
        return acc;
      }, {});
  
      const history = Object.values(groupedLogs).sort((a, b) =>
        new Date(b.created_at) - new Date(a.created_at)
      );
  
      console.log('Processed export history:', history);
      setExportHistory(history);
      
    } catch (err) {
      console.error('Error in loadExportHistory:', err);
      setError(err.message || 'Failed to load export history.');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  };
  
  // Fetch export history from integrations_log
  useEffect(() => {
    if (projectId && userId) {
      console.log('Loading export history for project:', projectId, 'user:', userId);
      loadExportHistory();
    }
  }, [projectId, userId]);

  // Toggle accordion open/close
  const toggleAccordion = (batchId) => {
    const newOpenAccordions = new Set(openAccordions);
    if (newOpenAccordions.has(batchId)) {
      newOpenAccordions.delete(batchId);
    } else {
      newOpenAccordions.add(batchId);
    }
    setOpenAccordions(newOpenAccordions);
  };

  // Format date nicely
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Get relative time (e.g., "2 days ago")
  const getRelativeTime = (dateString) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
      if (diffHours === 0) {
        const diffMinutes = Math.floor(diffTime / (1000 * 60));
        return `${diffMinutes} ${diffMinutes === 1 ? 'minute' : 'minutes'} ago`;
      }
      return `${diffHours} ${diffHours === 1 ? 'hour' : 'hours'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
    } else {
      return formatDate(dateString);
    }
  };

  // Get status icon based on status
  const StatusIcon = ({ status }) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'error':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'pending':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return null;
    }
  };

  // Get status summary for a batch
  const getStatusSummary = (logs) => {
    const total = logs.length;
    const success = logs.filter(log => log.status === 'success').length;
    const error = logs.filter(log => log.status === 'error').length;
    const pending = logs.filter(log => log.status === 'pending').length;
    
    return { total, success, error, pending };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-700">Loading export history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <XCircleIcon className="h-8 w-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-700 mb-2">{error}</p>
        <button
          onClick={loadExportHistory}
          className="inline-flex items-center px-3 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
        >
          <ArrowPathIcon className="h-4 w-4 mr-1" />
          Try Again
        </button>
      </div>
    );
  }

  if (exportHistory.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
        <DocumentIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
        <p className="text-gray-600">No export history available for this project.</p>
        <p className="text-gray-500 text-sm mt-2">
          Export history will appear here once measurements are sent to integrations.
        </p>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold text-gray-900">Export History</h2>
        <button
          onClick={loadExportHistory}
          disabled={refreshing}
          className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
        >
          <ArrowPathIcon className={`h-5 w-5 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </button>
      </div>

      <div className="space-y-2">
        {exportHistory.map((batch) => {
          const statusSummary = getStatusSummary(batch.logs);
          const projectCodes = [...new Set(batch.logs.map(log => log.project_code))];
          
          return (
            <div key={batch.export_batch_id} className="border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
              {/* Accordion Header */}
              <button
                onClick={() => toggleAccordion(batch.export_batch_id)}
                className="w-full flex justify-between items-center px-5 py-4 bg-white hover:bg-gray-50 focus:outline-none focus:bg-gray-50 transition-colors duration-200"
              >
                <div className="flex items-start">
                  <StatusIcon status={batch.status} />
                  <div className="flex flex-col text-left ml-3">
                    <span className="text-base font-medium text-gray-900">
                      {batch.logs.length} Measurements Sent to {batch.integration}
                    </span>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mt-2">
                      <span className="flex items-center">
                        <ClockIcon className="h-4 w-4 mr-1" />
                        {getRelativeTime(batch.created_at)}
                      </span>
                      <span className="flex items-center">
                        <DocumentDuplicateIcon className="h-4 w-4 mr-1" />
                        {projectCodes.length > 1 
                          ? `${projectCodes.length} Projects` 
                          : projectCodes[0] || 'Unknown Project'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="flex items-center space-x-1">
                    {statusSummary.success > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        {statusSummary.success} Successful
                      </span>
                    )}
                    {statusSummary.error > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                        {statusSummary.error} Failed
                      </span>
                    )}
                    {statusSummary.pending > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        {statusSummary.pending} Pending
                      </span>
                    )}
                  </div>
                  {openAccordions.has(batch.export_batch_id) ? (
                    <ChevronUpIcon className="h-5 w-5 text-gray-500" />
                  ) : (
                    <ChevronDownIcon className="h-5 w-5 text-gray-500" />
                  )}
                </div>
              </button>

              {/* Accordion Body */}
              {openAccordions.has(batch.export_batch_id) && (
                <div className="px-5 py-4 bg-white border-t border-gray-200">
                  <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                    <h4 className="font-medium text-gray-900 mb-3">Export Summary</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-gray-700 flex items-center mb-2">
                          <span className="font-medium mr-2">Date:</span> 
                          {formatDate(batch.created_at)}
                        </p>
                        <p className="text-sm text-gray-700 flex items-center">
                          <span className="font-medium mr-2">Integration:</span> 
                          {batch.integration}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-gray-700 flex items-center mb-2">
                          <span className="font-medium mr-2">Projects:</span>
                          {projectCodes.join(', ') || 'Unknown'}
                        </p>
                        <p className="text-sm text-gray-700 flex items-center">
                          <span className="font-medium mr-2">Status:</span>
                          <span className="flex items-center">
                            <StatusIcon status={batch.status} />
                            <span className={`ml-1 ${
                              batch.status === 'success' 
                                ? 'text-green-600' 
                                : batch.status === 'error' 
                                  ? 'text-red-600' 
                                  : 'text-yellow-600'
                            }`}>
                              {statusSummary.success} Successful, {statusSummary.error} Failed, {statusSummary.pending} Pending
                            </span>
                          </span>
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Status cards instead of table */}
                  <div className="grid grid-cols-1 gap-3">
                    {batch.logs.map((log, index) => (
                      <div 
                        key={`${log.measurement_id}-${index}`} 
                        className={`p-3 rounded-lg border ${
                          log.status === 'success' 
                            ? 'border-green-200 bg-green-50' 
                            : log.status === 'error'
                              ? 'border-red-200 bg-red-50'
                              : 'border-yellow-200 bg-yellow-50'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center">
                              <StatusIcon status={log.status} />
                              <h5 className="text-sm font-medium ml-2 text-gray-900">
                                Measurement: {log.measurement_id}
                              </h5>
                            </div>
                            <p className="mt-1 text-xs text-gray-700">
                              <span className="font-medium">Project:</span> {log.project_code || 'Unknown'}
                            </p>
                          </div>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                            ${log.status === 'success' 
                              ? 'bg-green-100 text-green-800' 
                              : log.status === 'error' 
                                ? 'bg-red-100 text-red-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                            {log.status.charAt(0).toUpperCase() + log.status.slice(1)}
                          </span>
                        </div>
                        {log.message && (
                          <p className="mt-2 text-sm text-gray-700">
                            {log.message}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}